// views/Profile/components/TierCatalogCard.jsx
import React, { useState } from 'react';
import {
  Check,
  ArrowUpRight,
  ChevronDown,
  Coins,
  Zap,
  Rocket,
  TrendingUp,
  Building2,
  Crown,
  Star,
  Loader2,
} from 'lucide-react';
import './TierCatalogCard.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatNumber = (value) => {
  if (value == null) return 'Unlimited';
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

// USD prices: integers stay clean ($199), non-integers always show
// 2 decimals ($49.90) so the catalog never mixes "$25" with "$49.9"
// across cards.
const formatUsd = (value) => {
  const isInt = Number.isInteger(value);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const formatPrice = (tier, billingCycle) => {
  // The per-org enterprise price is still loading — show a neutral
  // placeholder rather than the catalog's "Custom" (which would then flash
  // to the real price once it resolves).
  if (tier._pricePending) {
    return { primary: '—', suffix: null };
  }
  // displayPrice always wins (e.g. "Custom" for Enterprise rows whose
  // numeric prices are 0 by convention).
  if (tier.displayPrice) {
    return { primary: tier.displayPrice, suffix: null };
  }
  const monthly = tier.monthlyPriceUsd;
  if (monthly == null) {
    return { primary: 'Custom', suffix: null };
  }
  if (monthly === 0) {
    // "$0 / month" y no "Free": el plan ya se llama Free en el título de la
    // tarjeta, y repetirlo en el hueco del precio dejaba la misma palabra
    // dos veces seguidas en letra grande. Además el cero se compara con los
    // otros precios —misma unidad, misma fila— y "Free" no.
    return { primary: '$0', suffix: '/ month' };
  }
  if (
    billingCycle === 'yearly' &&
    tier.yearlyPriceUsd != null &&
    tier.yearlyPriceUsd > 0
  ) {
    // Yearly price is billed as one annual charge; the card always
    // displays an equivalent "/ month" rate so switching the toggle
    // doesn't change the unit prices are compared in, only the total.
    return {
      primary: `$${formatUsd(tier.yearlyPriceUsd / 12)}`,
      suffix: '/ month',
    };
  }
  return {
    primary: `$${formatUsd(monthly)}`,
    suffix: '/ month',
  };
};

// Enterprise's seed description leaks the org_subscriptions table name
// because it doubles as a backend dev hint. Override it for the catalog
// so prospects see customer-facing copy.
const ENTERPRISE_DESCRIPTION =
  'Tailored pricing and a coin allowance sized to your organization.';

// La descripción del backend repite el nombre del plan que ya está en el
// título: "Free tier for getting started", "Starter tier for small teams",
// "Pro tier for power users". Leído en la tarjeta eso es la tercera vez que
// aparece la misma palabra —etiqueta, nombre y bajada—, y lo único que
// distingue a un plan de otro es lo que viene después.
//
// Sólo se quita cuando el patrón está: "<algo> tier for ..." → "For ...".
// Si el texto no encaja se deja tal cual, que es lo que va a pasar con
// cualquier descripción que alguien escriba a mano más adelante.
const TIER_PREFIX = /^\S+\s+tier\s+(?=for\s)/i;

const trimTierPrefix = (text) => {
  if (typeof text !== 'string') return text;
  const trimmed = text.replace(TIER_PREFIX, '');
  if (trimmed === text) return text;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

// Maps each tier type to the icon shown in TierCatalogCard's circular
// icon badge — mirrors PackageCard's single Coins icon, but varied per
// tier since these represent different products, not one thing bought
// in different quantities.
const TIER_ICONS = {
  FREE: Zap,
  DEVELOPER: Rocket,
  STARTER: TrendingUp,
  PRO: Crown,
  ENTERPRISE: Building2,
};

/**
 * Quotas surfaced in the collapsible "See quotas" section. Coins and
 * price live above the fold — these are the secondary specs users only
 * skim once they care.
 */
const QUOTA_ROWS = [
  { key: 'maxWorkflows', label: 'Workflows', formatter: formatNumber },
  { key: 'maxDatabases', label: 'Databases', formatter: formatNumber },
  {
    key: 'maxAssistantRequestsPerMonth',
    label: 'Agent requests',
    formatter: formatNumber,
  },
];

/**
 * TierCatalogCard — one card of the plan catalog grid (all tiers,
 * including Enterprise, render with this same vertical card layout —
 * there is no separate banner treatment).
 *
 * Props:
 *   tier:       TierItem (camelCase, full backend shape).
 *   isCurrent:  boolean — when true, the card gets a violet border,
 *               a "Current plan" badge, and the primary CTA becomes
 *               an enabled "Manage plan" button instead of "Switch
 *               plan"/"Contact us"/"Downgrade".
 *   onSwitch:   () => void — fired for the "Switch plan" CTA on
 *               non-current, non-enterprise, non-free tiers.
 *   onContact:  () => void — fired for the Enterprise tier "Contact"
 *               CTA (only when NOT the current tier).
 *   onManage:   () => void — fired for the "Manage plan" CTA when
 *               `isCurrent` is true.
 */
export default function TierCatalogCard({
  tier,
  isCurrent = false,
  billingCycle = 'monthly',
  checkingOut = false,
  canManageEnterprise = false,
  onSwitch,
  onContact,
  onManage,
  onSubscribe,
}) {
  const [quotasOpen, setQuotasOpen] = useState(false);

  if (!tier) {
    return null;
  }

  const { primary: priceLabel, suffix: priceSuffix } = formatPrice(tier, billingCycle);
  const isEnterprise = tier.type === 'ENTERPRISE';
  const isFree = tier.type === 'FREE';
  const description = isEnterprise
    ? ENTERPRISE_DESCRIPTION
    : trimTierPrefix(tier.description);
  const quotasPanelId = `tier-quotas-${tier.id}`;

  let cta;
  if (isCurrent) {
    if (isEnterprise && canManageEnterprise) {
      // Org admin/owner on an enterprise plan: self-serve pay path.
      // The click starts the Tilopay checkout (SubscriptionView derives
      // "plan provisioned?" from the backend response).
      cta = (
        <button
          type="button"
          className="tier-card__cta tier-card__cta--subscribe"
          onClick={onSubscribe}
          disabled={checkingOut}
        >
          {checkingOut ? (
            <>
              <Loader2 size={14} aria-hidden="true" className="tier-card__spinner" />
              Redirecting…
            </>
          ) : (
            <>
              Subscribe · {priceLabel}
              {priceSuffix ? ` ${priceSuffix}` : ''}
            </>
          )}
        </button>
      );
    } else if (isEnterprise && !canManageEnterprise) {
      // Member of an enterprise-pool org: the plan is managed by an
      // admin — no self-serve pay CTA, just a plain current-plan marker.
      cta = (
        <span
          className="tier-card__cta tier-card__cta--current"
          aria-disabled="true"
        >
          Current plan
        </span>
      );
    } else {
      cta = (
        <button
          type="button"
          className="tier-card__cta tier-card__cta--manage"
          onClick={onManage}
        >
          Manage plan
        </button>
      );
    }
  } else if (isEnterprise) {
    cta = (
      <button
        type="button"
        className="tier-card__cta tier-card__cta--enterprise"
        onClick={onContact}
        disabled={checkingOut}
      >
        {checkingOut ? (
          <>
            <Loader2 size={14} aria-hidden="true" className="tier-card__spinner" />
            Redirecting…
          </>
        ) : (
          <>
            Contact us
            <ArrowUpRight size={14} aria-hidden="true" />
          </>
        )}
      </button>
    );
  } else if (isFree) {
    cta = (
      <button
        type="button"
        className="tier-card__cta tier-card__cta--downgrade"
        onClick={onSwitch}
        disabled={checkingOut}
      >
        {checkingOut ? (
          <>
            <Loader2 size={14} aria-hidden="true" className="tier-card__spinner" />
            Redirecting…
          </>
        ) : (
          'Downgrade'
        )}
      </button>
    );
  } else {
    cta = (
      <button
        type="button"
        className="tier-card__cta tier-card__cta--switch"
        onClick={onSwitch}
        disabled={checkingOut}
      >
        {checkingOut ? (
          <>
            <Loader2 size={14} aria-hidden="true" className="tier-card__spinner" />
            Redirecting…
          </>
        ) : (
          'Switch plan'
        )}
      </button>
    );
  }

  const TierIcon = TIER_ICONS[tier.type] || Coins;

  return (
    <article
      className={`tier-card${
        isCurrent ? ' tier-card--current' : ''
      }${isEnterprise ? ' tier-card--enterprise' : ''}`}
    >
      {tier.type === 'PRO' && (
        <span className="tier-card__ribbon">
          <Star size={12} aria-hidden="true" />
          Recommended
        </span>
      )}

      <div className="tier-card__icon">
        <TierIcon size={28} aria-hidden="true" />
      </div>

      {/* Sin la píldora del tipo: decía "PRO" encima de un título que ya
          dice "Professional". Era la misma palabra dos veces —tres con la
          bajada— y ninguna de las tres añadía nada a las otras. El nombre
          se queda solo y más grande, que es lo que se lee para saber qué
          plan es. */}
      <header className="tier-card__header">
        <div className="tier-card__type-row">
          <h3 className="tier-card__name">{tier.displayName}</h3>
          {isCurrent && (
            <span className="tier-card__active-badge">
              <Check size={12} aria-hidden="true" />
              Current plan
            </span>
          )}
        </div>
        <p className="tier-card__description">{description}</p>
      </header>

      {/* Enterprise normally has no fixed price — the description says
       * "Tailored pricing" and the coin block surfaces "Custom", so the
       * price block is hidden to avoid stamping another "Custom". BUT when
       * the org has a negotiated per-org price (displayPrice set, e.g.
       * "$1,500/mo") we DO show it — that's a real number worth surfacing.
       * While it's still loading (_pricePending) we also show the slot so
       * the placeholder holds the layout instead of popping in. */}
      {(!isEnterprise ||
        (tier.displayPrice && tier.displayPrice !== 'Custom') ||
        tier._pricePending) && (
      <div className="tier-card__price">
        <span className="tier-card__price-line">
          <span className="tier-card__price-primary">{priceLabel}</span>
          {priceSuffix && (
            <span className="tier-card__price-suffix">{priceSuffix}</span>
          )}
        </span>
        {/* The secondary line keeps the slot height stable across
         * cards (paid tiers show the annual total; Free leans on a
         * positive microcopy so the bottom of the slot doesn't read
         * as a gap). When billingCycle is 'yearly' the primary line
         * above already shows the discounted /month rate, so this
         * line clarifies it's billed as one annual charge. */}
        {tier.yearlyPriceUsd != null && tier.yearlyPriceUsd > 0 ? (
          <span className="tier-card__price-yearly">
            {billingCycle === 'yearly'
              ? `$${formatUsd(tier.yearlyPriceUsd)} billed yearly`
              : `$${formatUsd(tier.yearlyPriceUsd)} / year`}
          </span>
        ) : isFree ? (
          <span className="tier-card__price-yearly">
            No credit card required
          </span>
        ) : null}
      </div>
      )}

      <div className="tier-card__coins">
        <Coins size={16} aria-hidden="true" className="tier-card__coins-icon" />
        {isEnterprise ? (
          <>
            <span className="tier-card__coins-value tier-card__coins-value--custom">
              Custom
            </span>
            <span className="tier-card__coins-label">coin allowance</span>
          </>
        ) : (
          <>
            <span className="tier-card__coins-value">
              {formatCoins(tier.monthlyCoinAllowance)}
            </span>
            <span className="tier-card__coins-label">coins / month</span>
          </>
        )}
      </div>

      {/* Enterprise's quota fields are placeholder zeros in the seed
       * because the real numbers live in organization_subscriptions —
       * showing them as "0 workflows / 0 databases" is actively
       * misleading, so the disclosure is hidden for that tier. */}
      {!isEnterprise && (
        <div className="tier-card__quotas">
          <button
            type="button"
            className="tier-card__quotas-toggle"
            onClick={() => setQuotasOpen((o) => !o)}
            aria-expanded={quotasOpen}
            aria-controls={quotasPanelId}
          >
            <span>{quotasOpen ? 'Hide quotas' : 'See quotas'}</span>
            <ChevronDown
              size={14}
              aria-hidden="true"
              className={`tier-card__quotas-chevron${
                quotasOpen ? ' tier-card__quotas-chevron--open' : ''
              }`}
            />
          </button>
          <div
            id={quotasPanelId}
            className={`tier-card__quotas-panel${
              quotasOpen ? ' tier-card__quotas-panel--open' : ''
            }`}
            role="region"
          >
            <ul className="tier-card__features">
              {QUOTA_ROWS.map((row) => (
                <li key={row.key} className="tier-card__feature">
                  <span className="tier-card__feature-label">{row.label}</span>
                  <span className="tier-card__feature-value">
                    {row.formatter(tier[row.key])}
                  </span>
                </li>
              ))}
              <li className="tier-card__feature">
                <span className="tier-card__feature-label">RAG</span>
                <span className="tier-card__feature-value">
                  {tier.ragEnabled ? (
                    <Check size={14} aria-hidden="true" className="tier-card__yes" />
                  ) : (
                    <span className="tier-card__no">—</span>
                  )}
                </span>
              </li>
            </ul>
          </div>
        </div>
      )}

      <div className="tier-card__cta-wrapper">{cta}</div>
    </article>
  );
}
